/** @jsxImportSource @emotion/react */
import { forwardRef, useImperativeHandle, useRef } from 'react';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import * as S from './styled/Badge.styled';

/**
 * @author @muhammedburakak
 * @description Badges are smaill components used for another element to show quick info.
 * @param {Object} props - props of the component.
 * @param {'standart' | 'small'} [props.size] - The size of the component.
 * @param {'dot' | 'notification' | 'pill'} [props.variant] - The variant of the badge.
 * @param {'blue' | 'red' | 'yellow' | 'green' | 'black' | 'teel' | 'white' | 'light'} [props.color] - The background color of the badge.
 * @param {string} [props.text] - The info text of the badge, you use either icon or text.
 * @param {string} [props.className] - Adds classname to the component. Override or extend the styles applied to the component.
 * @param {Object} [props.css] - Adds css to the styled component. Override or extend the styles applied to the component.
 * @param {Object} [props.icon] - Icon for the badge. You can use any icon from FontAwesome or you use text.
 * @param {'x-small' |'small' | 'medium' | 'large'} [props.iconSize] - IconSize for the badge.
 *  * @param {*} [props.children] - The content of the component. If it's empty only label and icon will be shown.
 * @returns {object} - Badge object with the given props.
 * @example
 * import Badge from 'components/field/Badge';
 * import { css } from '@emotion/react';
 *
 * <Badge
 * 	size="large"
 * 	color='red'
 * 	text='1'
 * 	className="myStyle"
 * 	css={`background-color: red !important;`}
 * 	variant="pill"
 * 	icon={faCircleNotch}
 * 	iconSize="large"
 *  size="standart"
 * />
 */
const Badge = (props, ref) => {
	const {
		size = 'standart',
		text,
		css,
		color,
		variant,
		icon,
		iconSize,
		children,
		anchorOrigin,
		className,
		...moreProps
	} = props;

	const innerRef = useRef(null);

	useImperativeHandle(ref, () => innerRef.current);

	return (
		<S.Container className="badge-container">
			{!(variant === 'notification' && text === '') && (
				<S.BadgeContainer
					ref={innerRef}
					anchorOrigin={anchorOrigin}
					className={`badge ${color} ${children ? 'hasChildren' : ''} 
					${variant === 'pill' || variant === 'dot' ? `${variant}-${size}` : 'notifySize'}
					${variant === 'pill' || variant === 'dot' ? variant : 'red'}
					${className || ''} `}
					css={css}
					icon={icon}
					{...moreProps}>
					<>
						{icon && <Icon icon={icon} size={iconSize} />}
						{text && <Typography fontWeight={500}>{text}</Typography>}
					</>
				</S.BadgeContainer>
			)}

			{children && (
				<S.ChildrenContainer $hasNotifyBadge={!(variant === 'notification' && text === '')}>
					{children}
				</S.ChildrenContainer>
			)}
		</S.Container>
	);
};
Badge.displayName = 'Badge';

export default forwardRef(Badge);
